import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User as UserIcon, Lock, AlertCircle, Loader } from 'lucide-react';
import { supabase } from '../config/supabase';
import { Button, AdidasLogo } from '../components/shared';

export const TechnicianLogin: React.FC = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            // Login con Supabase Auth
            const { data, error: authError } = await supabase.auth.signInWithPassword({
                email: email.trim(),
                password: password
            });

            if (authError) throw authError;

            // Verificar que el usuario sea técnico
            const { data: userData, error: userError } = await supabase
                .from('users')
                .select('role, name')
                .eq('email', email.trim())
                .single();

            if (userError) throw userError;

            if (userData.role !== 'TECH') {
                await supabase.auth.signOut();
                throw new Error('Solo técnicos pueden acceder a esta página');
            }

            // Guardar datos del técnico en localStorage para acceso rápido
            localStorage.setItem('technician_name', userData.name);

            // Verificar si hay un token pendiente de verificación
            const pendingToken = sessionStorage.getItem('pending_verification_token');
            if (pendingToken) {
                sessionStorage.removeItem('pending_verification_token');
                navigate(`/verify/${pendingToken}`);
            } else {
                // Redirigir al dashboard
                navigate('/');
            }
        } catch (err: any) {
            console.error('Login error:', err);
            setError(err.message || 'Error al iniciar sesión');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                {/* Header */}
                <div className="bg-black text-white p-8 text-center">
                    <div className="flex justify-center mb-4">
                        <AdidasLogo className="w-16 h-16" />
                    </div>
                    <h1 className="text-2xl font-bold mb-2">Acceso Técnico</h1>
                    <p className="text-gray-300 text-sm">
                        Sistema de Verificación QR
                    </p>
                </div>

                {/* Form */}
                <form onSubmit={handleLogin} className="p-8 space-y-6">
                    {/* Email Field */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <UserIcon className="text-gray-400" size={20} />
                            </div>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-black focus:outline-none text-base"
                                placeholder="tecnico@hardworks.cl"
                                required
                                autoComplete="email"
                                autoFocus
                            />
                        </div>
                    </div>

                    {/* Password Field */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Contraseña
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Lock className="text-gray-400" size={20} />
                            </div>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-black focus:outline-none text-base"
                                placeholder="••••••••"
                                required
                                autoComplete="current-password"
                            />
                        </div>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 flex items-start gap-3">
                            <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                            <p className="text-sm text-red-800">{error}</p>
                        </div>
                    )}

                    {/* Submit Button */}
                    <Button
                        type="submit"
                        disabled={loading || !email || !password}
                        className="w-full"
                        size="lg"
                    >
                        {loading ? (
                            <>
                                <Loader className="animate-spin mr-2" size={20} />
                                Ingresando...
                            </>
                        ) : (
                            'Ingresar'
                        )}
                    </Button>

                    {/* Info */}
                    <div className="text-center">
                        <p className="text-xs text-gray-500">
                            Solo técnicos autorizados pueden acceder
                        </p>
                    </div>
                </form>

                {/* Footer */}
                <div className="bg-gray-50 px-8 py-4 border-t text-center">
                    <p className="text-xs text-gray-500">
                        © 2026 Adidas Tickets - HardWorks
                    </p>
                </div>
            </div>
        </div>
    );
};
